/**
 * The language Ìran speaks TO you — which is not the language your family is
 * keeping alive.
 *
 * Those two are different things and this product needs both. A Yorùbá family
 * in Paris is preserving Yorùbá and reading French. Before this, the heritage
 * language was handled (see terms.ts) and the interface was hardcoded English,
 * so that family got their own language in the greeting and nowhere else.
 *
 * Deliberately small and dependency-free: a catalogue per locale, lazily
 * imported, with English underneath everything. The rule that matters is at the
 * bottom of `translate` — a missing or empty translation falls back to English,
 * never to the key. A grandmother must never be shown `nav.storyVault`.
 *
 * Translations are held to the same standard as heritage terms: a catalogue
 * ships only once a speaker of that language has reviewed it. Machine output is
 * not published here, in a product whose whole premise is that a machine's
 * words must not pass as a person's.
 */
import { useSyncExternalStore } from 'react';
import en from '../locales/en.json';

export type Catalogue = Record<string, string>;

/** Only locales with a reviewed catalogue appear here. */
export const LOCALES: Array<{ code: string; label: string; english: string; rtl?: boolean }> = [
  { code: 'en', label: 'English', english: 'English' },
];

const KEY = 'iran.locale.v1';
const loaded: Record<string, Catalogue> = { en: en as Catalogue };
let current = 'en';
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

function supported(code: string): string | null {
  if (LOCALES.some((l) => l.code === code)) return code;
  const base = code.split('-')[0]!;
  return LOCALES.some((l) => l.code === base) ? base : null;
}

/** Stored choice first, then the browser's own languages, then English. */
export function detectLocale(): string {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored && supported(stored)) return supported(stored)!;
  } catch {
    // Storage unavailable; fall through to the browser's preference.
  }
  for (const tag of typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language]) {
    const hit = supported(tag);
    if (hit) return hit;
  }
  return 'en';
}

export function getLocale(): string {
  return current;
}

export async function setLocale(code: string): Promise<void> {
  const target = supported(code) ?? 'en';
  if (!loaded[target]) {
    try {
      loaded[target] = (await import(`../locales/${target}.json`)).default as Catalogue;
    } catch {
      // A catalogue that will not load is not a reason to break the page.
      // English is already loaded and is a complete, correct interface.
      return;
    }
  }
  current = target;
  try {
    localStorage.setItem(KEY, target);
  } catch {
    // Not remembered, but applied for this visit.
  }
  const meta = LOCALES.find((l) => l.code === target);
  document.documentElement.lang = target;
  document.documentElement.dir = meta?.rtl ? 'rtl' : 'ltr';
  emit();
}

/**
 * `vars` are substituted as {name}. Interpolation happens after lookup so a
 * translator never has to reproduce a value, only place it.
 */
export function translate(key: string, vars?: Record<string, string | number>): string {
  const table = loaded[current] ?? {};
  const fallback = en as Catalogue;
  const raw = table[key]?.trim() || fallback[key]?.trim() || '';
  if (!raw) {
    // A key with no English either is a bug in the catalogue, not something to
    // put in front of a person. Say nothing rather than show machinery.
    if (import.meta.env.DEV) console.warn(`[i18n] no string for "${key}"`);
    return '';
  }
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Re-renders the component when the interface language changes. */
export function useT(): { t: typeof translate; locale: string } {
  const locale = useSyncExternalStore(subscribe, getLocale, () => 'en');
  return { t: translate, locale };
}
